import {useCallback} from 'react';
import {useSearchParams} from 'react-router-dom';
import {cn} from '@tinct/core-utils';

export type StudioTab = 'original' | 'variants';

/**
 * The studio's mode lives in the URL (`?view=original|variants`) so the
 * top-bar tabs — rendered by the hosts (funnel top bar, management studio
 * header) — and the studio itself stay in sync without extra plumbing. A
 * `?pageId=` deep link with no explicit `view` opens the variants side.
 */
export function useStudioTab(): [StudioTab, (tab: StudioTab) => void] {
  const [searchParams, setSearchParams] = useSearchParams();

  const view = searchParams.get('view');
  const activeTab: StudioTab =
    view === 'variants' || (view === null && searchParams.has('pageId'))
      ? 'variants'
      : 'original';

  const setActiveTab = useCallback(
    (tab: StudioTab) => {
      setSearchParams(
        (params) => {
          params.set('view', tab);
          return params;
        },
        {replace: true},
      );
    },
    [setSearchParams],
  );

  return [activeTab, setActiveTab];
}

type StudioModeTabsProps = {
  /** Number of generated variant pages, shown as a badge on the variants tab. */
  variantsCount: number;
  className?: string;
};

/**
 * "Original page | Page variants" segmented control — the studio's mode
 * switch, designed to sit in the top bar next to the campaign name.
 */
export const StudioModeTabs = ({variantsCount, className}: StudioModeTabsProps) => {
  const [activeTab, setActiveTab] = useStudioTab();

  const triggerClass = (tab: StudioTab) =>
    cn(
      'flex h-full min-w-0 flex-1 items-center justify-center gap-2 rounded-sm px-2 py-1 text-sm font-medium whitespace-nowrap transition-colors',
      activeTab === tab
        ? 'bg-background text-foreground shadow-sm'
        : 'text-muted-foreground hover:text-foreground',
    );

  return (
    <div
      className={cn(
        'flex h-9 w-72 shrink-0 cursor-pointer items-center rounded-lg bg-muted p-[3px]',
        className,
      )}
    >
      <button
        type="button"
        className={triggerClass('original')}
        onClick={() => setActiveTab('original')}
      >
        Original page
      </button>
      <button
        type="button"
        className={triggerClass('variants')}
        onClick={() => setActiveTab('variants')}
      >
        Page variants
        {variantsCount > 0 && (
          <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-xs font-semibold text-primary-foreground">
            {variantsCount}
          </span>
        )}
      </button>
    </div>
  );
};
